import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Table, TextLink, Tray, useModalControl } from '@frontend/design-system';
import { DevicePreviewModal } from './device-preview-modal';
import { commonLabels } from './labels';
import { AnalyticsDataKey, type GroupItemsProp } from './types';
import { calculatePercentage } from './utils';
import { SubduedText } from '.';

export const ChartTableView = ({
  filterHintText,
  entityType,
  categoryBarChartData,
  customNameHeaders,
  title,
  children,
}: GroupItemsProp) => {
  const { t } = useTranslation('analytics-phone');
  const [modalState, setModalState] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const { modalProps, triggerProps } = useModalControl();

  const mappedData = useMemo(() => {
    return Object.keys(categoryBarChartData).map((id) => ({
      id,
      ...categoryBarChartData[id],
      name: customNameHeaders ? customNameHeaders[id]?.name || id : id,
    }));
  }, [categoryBarChartData, customNameHeaders]);

  const colConfig = useMemo(() => {
    return [
      {
        id: 'name',
        Header: t('Call {{ entityType }}', { entityType }),
        accessor: (row: (typeof mappedData)[number]) => row.name,
      },
      {
        id: 'devices',
        Header: commonLabels.devices,
        accessor: (row: (typeof mappedData)[number]) => row,
        cellRenderer: ({ id }: { id: string }) => (
          <TextLink
            onClick={() => {
              triggerProps.onClick();
              setModalState({
                id,
                name: customNameHeaders?.[id].name ?? '',
              });
            }}
          >
            {t('{{ count }} Devices', { count: customNameHeaders?.[id].devicesCount })}
          </TextLink>
        ),
      },
      {
        id: AnalyticsDataKey.TotalCalls,
        Header: t('Total Calls', { ns: 'analytics-phone' }),
        accessor: (row: (typeof mappedData)[number]) => row.totalCalls,
      },
      {
        id: AnalyticsDataKey.AnsweredCalls,
        Header: t('Answered Calls', { ns: 'analytics-phone' }),
        accessor: (row: (typeof mappedData)[number]) => row.answeredCalls,
      },
      {
        id: AnalyticsDataKey.MissedCalls,
        Header: t('Missed Calls', { ns: 'analytics-phone' }),
        accessor: (row: (typeof mappedData)[number]) => row.missedCalls,
      },
      {
        id: AnalyticsDataKey.AbandonedCalls,
        Header: t('Abandoned Calls', { ns: 'analytics-phone' }),
        accessor: (row: (typeof mappedData)[number]) => row.abandonedCalls,
      },
      {
        id: 'Answer Rate',
        Header: commonLabels.answerRate,
        accessor: (row: (typeof mappedData)[number]) => row,
        cellRenderer: ({ totalCalls, answeredCalls }: { totalCalls: number; answeredCalls: number }) => {
          const answerRate = calculatePercentage(answeredCalls, totalCalls);
          return <span>{answerRate}%</span>;
        },
      },
      {
        id: 'Missed Rate',
        Header: commonLabels.missedRate,
        accessor: (row: (typeof mappedData)[number]) => row,
        cellRenderer: ({ totalCalls, missedCalls }: { totalCalls: number; missedCalls: number }) => {
          const missedRate = calculatePercentage(missedCalls, totalCalls);
          return <span>{missedRate}%</span>;
        },
      },
      {
        id: 'Abandoned Rate',
        Header: commonLabels.abandonedRate,
        accessor: (row: (typeof mappedData)[number]) => row,
        cellRenderer: ({ totalCalls, abandonedCalls }: { totalCalls: number; abandonedCalls: number }) => {
          const abandonedRate = calculatePercentage(abandonedCalls, totalCalls);
          return <span>{abandonedRate}%</span>;
        },
      },
    ];
  }, [entityType, t, triggerProps, customNameHeaders]);

  return (
    <div css={containerStyle}>
      <div css={headerStyle}>
        <Heading level={3}>
          {title}
          <SubduedText weight='regular'>
            {t('Each column represents a segment of calls that rang the specified Call {{ entityType }} in that row.', {
              entityType,
            })}
          </SubduedText>
        </Heading>
        <SubduedText color='light' weight='bold' size='medium'>
          {filterHintText}
        </SubduedText>
      </div>
      {children ? children : <Table data={mappedData} isPaginated colConfig={colConfig} />}

      <Tray {...modalProps} width='medium'>
        <DevicePreviewModal entityType={entityType} modalState={modalState} onClose={modalProps.onClose} />
      </Tray>
    </div>
  );
};

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  border: 1px solid ${theme.colors.neutral20};
  padding: ${theme.spacing(3)};
`;

const headerStyle = css`
  display: flex;
  justify-content: space-between;
`;
